'use client';

import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import PropTypes from 'prop-types';

/**
 * Component that redirects premium users from welcome to dashboard
 * and non-authenticated users to the sign-in page
 * NON-BLOCKING: Renders children immediately while checking status in background
 */
export default function PremiumRedirect({ children }) {
  const [user, setUser] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [checksComplete, setChecksComplete] = useState(false);
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        
        // Check for cached premium status first
        const cacheKey = `premium_status_${currentUser.uid}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        
        if (cachedData) {
          try {
            const { eligible, timestamp } = JSON.parse(cachedData);
            // Cache is valid for 5 minutes
            if (Date.now() - timestamp < 5 * 60 * 1000) {
              setIsPremium(eligible);
              setChecksComplete(true);
              return;
            }
          } catch (error) {
            console.error('Error parsing cached premium status:', error);
          }
        }
        
        // Check premium status in background
        try {
          const response = await fetch('/api/checkPremiumEligibility', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid: currentUser.uid }),
          });
          
          if (response.ok) {
            const { eligible } = await response.json();
            setIsPremium(eligible);
            
            // Cache the result
            sessionStorage.setItem(cacheKey, JSON.stringify({
              eligible,
              timestamp: Date.now()
            }));
          }
        } catch (error) {
          console.error('Error checking premium status:', error);
        }
        setChecksComplete(true);
      } else {
        // Not logged in
        setUser(null);
        setChecksComplete(true);
      }
    });
    
    return () => unsubscribe();
  }, []);
  
  // Not logged in - redirect to sign in
  if (checksComplete && !user) {
    return <Navigate to="/" replace />;
  }
  
  // Premium user on welcome page - redirect to dashboard
  if (checksComplete && isPremium && window.location.pathname === '/welcome') {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Render children immediately (non-blocking)
  return children;
} 

PremiumRedirect.propTypes = {
  children: PropTypes.node.isRequired
}; 